import type {
  EssayKind,
  OptionLetter,
  Question,
  QuestionVersion,
  RichText,
} from '@titularis/api-client';
import type { SelecaoDeAssuntos } from '@titularis/ui';
import { LETRAS } from '../../dominio';

/**
 * O conteúdo de uma questão, num lugar só: como ele sai da versão salva, como
 * ele volta para a API e o que conta como tê-lo alterado.
 *
 * As três coisas eram três definições espalhadas pela tela, e a terceira era a
 * mais frágil: "alterado" era um retrato do formulário inteiro em JSON, tirado
 * logo depois de preenchê-lo. Qualquer controle que devolvesse o mesmo conteúdo
 * escrito de outro jeito - a chave numa ordem, `null` onde antes não havia
 * chave, um `attrs` vazio que o editor acrescenta - virava alteração pendente,
 * com o botão de salvar ligado e a pergunta de descartar sem nada para
 * descartar.
 */

export interface FormularioDeQuestao {
  examId: string;
  position?: number;
  /** N disciplinas por questão. */
  disciplineIds?: string[];
  /** Assuntos e delimitações num campo só, na árvore da classificação. */
  assuntos?: SelecaoDeAssuntos;
  /** Só nas discursivas: dissertação, peça prática ou questão discursiva. */
  essayKind?: EssayKind;
  label?: string | null;
  statement?: RichText | null;
  /** Espelho da banca (padrão oficial da prova), só nas discursivas. */
  answerKey?: RichText | null;
  /** Modelo de Resposta (resposta ideal do especialista), só nas discursivas. */
  answerModel?: RichText | null;
  /** A letra vem da ordem: o primeiro item é "a", o segundo "b". */
  options?: { content?: RichText | null; commentary?: RichText | null }[];
  correctOption?: OptionLetter;
  commentary?: Record<string, RichText | null> | null;
}

/** Os campos do formulário como a versão salva os define. */
export function valoresDaVersao(questao: Question, versao: QuestionVersion): FormularioDeQuestao {
  return {
    examId: questao.examId,
    position: versao.position ?? undefined,
    disciplineIds: versao.disciplines.map((item) => item.id),
    assuntos: {
      subjectIds: versao.subjects.map((item) => item.id),
      subjectDetailIds: versao.subjectDetails.map((item) => item.id),
    },
    essayKind: versao.essayKind ?? undefined,
    label: versao.label,
    statement: versao.statement,
    answerKey: versao.answerKey,
    answerModel: versao.answerModel,
    options: (versao.options ?? []).map((item) => ({
      content: item.content,
      commentary: item.commentary,
    })),
    correctOption: versao.correctOption ?? undefined,
    commentary: desmontarComentario(versao.commentary),
  };
}

/**
 * O comentário vem da API com o documento e o HTML juntos; o editor só conhece
 * o documento. As três abas aparecem sempre, mesmo vazias, para o formulário
 * poder esvaziar uma que existia - ausência e vazio dizem coisas diferentes ao
 * salvar.
 */
function desmontarComentario(
  commentary: Record<string, { doc?: RichText } | undefined> | null | undefined,
): Record<string, RichText | null> {
  return {
    legislation: commentary?.legislation?.doc ?? null,
    jurisprudence: commentary?.jurisprudence?.doc ?? null,
    doctrine: commentary?.doctrine?.doc ?? null,
  };
}

/**
 * O conteúdo que vai para a API.
 *
 * Objetiva e discursiva mandam campos diferentes: o que não é do tipo da prova
 * fica de fora em vez de ir nulo, porque nulo é ordem de apagar.
 */
export function conteudoParaSalvar(valores: FormularioDeQuestao, discursiva: boolean) {
  return {
    position: valores.position ?? null,
    disciplineIds: valores.disciplineIds ?? [],
    subjectIds: valores.assuntos?.subjectIds ?? [],
    subjectDetailIds: valores.assuntos?.subjectDetailIds ?? [],
    commentary: valores.commentary ?? null,
    ...(discursiva
      ? {
          essayKind: valores.essayKind ?? null,
          label: valores.label ?? null,
          statement: valores.statement ?? null,
          answerKey: valores.answerKey ?? null,
          answerModel: valores.answerModel ?? null,
        }
      : {
          statement: valores.statement ?? null,
          /*
           * A letra é a posição na lista: mover um item renomeia os dois. A
           * letra sai do índice antes de a lista em branco cair fora, e não
           * depois, para o gabarito continuar apontando o mesmo item.
           */
          options: (valores.options ?? [])
            .map((item, indice) => ({
              letter: LETRAS[indice]!,
              content: item.content,
              commentary: item.commentary ?? null,
            }))
            .filter((item) => item.content),
          correctOption: valores.correctOption ?? null,
        }),
  };
}

/** Item sem enunciado não vira alternativa: é uma linha que ninguém preencheu. */
function itensPreenchidos(valores: FormularioDeQuestao) {
  return (valores.options ?? []).filter((item) => item.content);
}

/**
 * A assinatura do conteúdo: dois formulários com a mesma assinatura salvariam a
 * mesma versão.
 *
 * É o que responde "há o que salvar?", e por isso ela olha o conteúdo, e não o
 * formulário: campo que não vai para a API não entra, e o que entra passa pela
 * forma canônica, onde ausência, `null` e objeto vazio são a mesma coisa e a
 * ordem das chaves não existe. Assim o editor devolver o mesmo documento
 * escrito de outro jeito não vira alteração - e uma alteração de verdade nunca
 * deixa de virar, porque ela muda o que seria salvo.
 *
 * Os dois tipos de prova entram juntos: a assinatura não pode depender de saber
 * se a prova é discursiva, que é uma consulta à parte e chega depois da questão.
 *
 * A prova fica de fora: ela é identidade da questão, e não conteúdo da versão -
 * e sozinha não é o que salvar. Numa questão nova, escolher a prova não pode
 * acender o botão de cadastrar uma questão ainda sem nada dentro.
 */
export function assinaturaDoConteudo(valores: FormularioDeQuestao | undefined): string {
  if (!valores) return '';

  return JSON.stringify(
    canonico({
      position: valores.position,
      disciplineIds: valores.disciplineIds,
      subjectIds: valores.assuntos?.subjectIds,
      subjectDetailIds: valores.assuntos?.subjectDetailIds,
      essayKind: valores.essayKind,
      label: valores.label,
      statement: valores.statement,
      answerKey: valores.answerKey,
      answerModel: valores.answerModel,
      options: itensPreenchidos(valores).map((item) => ({
        content: item.content,
        commentary: item.commentary,
      })),
      correctOption: valores.correctOption,
      commentary: valores.commentary,
    }),
  );
}

/**
 * A assinatura de uma questão sem nada escrito - a régua da questão nova.
 * Escolher a prova não muda esta assinatura: prova é identidade da questão, e
 * não conteúdo da versão.
 */
export const ASSINATURA_VAZIA = assinaturaDoConteudo({} as FormularioDeQuestao);

/**
 * Forma canônica: o mesmo conteúdo escrito sempre do mesmo jeito.
 *
 * Chaves em ordem alfabética, e fora tudo que não diz nada - `undefined`,
 * `null` e objeto vazio. Os três aparecem sozinhos no caminho de ida e volta do
 * conteúdo: a API devolve `null` onde o formulário não tinha chave, e o editor
 * acrescenta `attrs` vazio a nós que vieram sem ele. Nenhum deles muda o que o
 * aluno lê, então nenhum deles é alteração.
 */
function canonico(valor: unknown): unknown {
  if (Array.isArray(valor)) return valor.map(canonico);
  if (valor === null || typeof valor !== 'object') return valor;

  const entradas = Object.entries(valor as Record<string, unknown>)
    .map(([chave, item]) => [chave, canonico(item)] as const)
    .filter(([, item]) => item !== undefined && item !== null && !ehObjetoVazio(item))
    .sort(([a], [b]) => a.localeCompare(b));

  return Object.fromEntries(entradas);
}

function ehObjetoVazio(valor: unknown): boolean {
  return (
    typeof valor === 'object' &&
    valor !== null &&
    !Array.isArray(valor) &&
    Object.keys(valor).length === 0
  );
}
